// @flow
import * as React from 'react';
import pluralize from 'pluralize';

import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import BoxBody from 'components/BoxBody';
import BoxHeader from 'components/BoxHeader';
import Button from 'components/Button';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import List from 'components/List';
import ListItem from 'components/ListItem';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextBadge from 'components/TextBadge';
import TextWithGlyph from 'components/TextWithGlyph';
import Time from 'components/Time';

type Props = {
  fileUpload: *,
};

class AdminUploadedFileDetail extends React.PureComponent<Props, *> {
  getTitleForErrorCode = (code: *) => {
    const mapCodeToTitle = {
      corrupted_file: 'Corrupted file',
      not_a_csv: 'Not a CSV file',
      no_student_number_column: 'No student_number column',
      off_hours: 'Off hours',
      too_many_attempts: 'Too many uploads',
      no_students_match: 'No matching student numbers',
      auto_message_not_found: 'No auto message found',
      too_many_student_numbers: 'Too many student numbers',
      missing_custom_message_fields: 'Missing data',
    };
    return mapCodeToTitle[code] || 'Error';
  };

  renderErrorList = (errors: *, fileUrl: *) => {
    return (
      <Text size="xsmall">
        <Row size="small">
          <Heading levelStyle="6">{pluralize('Error', errors.length)}</Heading>
        </Row>
        <Row>
          <List marker="number" size="medium">
            {errors.map(
              error =>
                error ? (
                  <ListItem key={error.code}>
                    <Text weight="semibold" data-test="error-list-item">
                      {this.getTitleForErrorCode(error.code)}:{' '}
                    </Text>
                    {error.description}
                  </ListItem>
                ) : null
            )}
          </List>
        </Row>
        <Row size="small">
          <Heading levelStyle="6">Next steps</Heading>
        </Row>
        <List marker="number" size="medium">
          <ListItem>
            <Link href={fileUrl}>
              <Text weight="semibold">Download the file</Text>
            </Link>{' '}
            Remind received
          </ListItem>
          <ListItem>
            Refer to this{' '}
            <Link href={LinkConstants.AUTO_MESSAGING_HELP} target="_blank">
              <Text weight="semibold">support article</Text>
            </Link>{' '}
            and make sure your CSV is formatted correctly
          </ListItem>
          <ListItem>
            Transfer the file via SFTP, at the time you want the auto message to send
          </ListItem>
        </List>
      </Text>
    );
  };

  render() {
    const { fileUpload } = this.props;
    if (!fileUpload) {
      return (
        <ErrorReporter error="AdminUploadedFileDetail - tried to render without a fileUpload" />
      );
    }
    const { fileName, fileUrl, errors, uploadedAt } = fileUpload;

    return (
      <React.Fragment>
        <BoxHeader
          textAlign="left"
          title={
            <MediaObject figure="right" center>
              <div>
                <Heading>{fileName}</Heading>
                <TextBadge
                  textColor={errors && errors.length > 0 ? 'lava' : 'pitch'}
                  textSize="xxxxsmall">
                  Received:{' '}
                  <Time
                    hour="2-digit"
                    minute="2-digit"
                    month="short"
                    day="numeric"
                    date={new Date(uploadedAt)}
                  />
                </TextBadge>
              </div>
              <Button level="secondary" href={fileUrl}>
                <TextWithGlyph glyph="download">Download</TextWithGlyph>
              </Button>
            </MediaObject>
          }
        />
        {errors &&
          errors.length > 0 && (
            <BoxBody data-test="error-content">
              <Row>
                <Divider size="flush" />
              </Row>
              <AdminNoticeBanner
                alwaysShowDetails
                icon="explanation-circle"
                color="lava"
                details={this.renderErrorList(errors, fileUrl)}>
                <Heading flush levelStyle="5">
                  There are errors with your file
                </Heading>
              </AdminNoticeBanner>
            </BoxBody>
          )}
      </React.Fragment>
    );
  }
}

export default AdminUploadedFileDetail;
